import { create } from "zustand";

import type { Gap } from "@/types/audit";
import type { GapFillCandidate } from "@/types/gapFill";

export type GapFillStep = 0 | 1 | 2 | 3;

interface GapFillState {
  conceptId: string | null;
  jobId: string | null;
  isPending: boolean;
  isCompleted: boolean;
  isOpen: boolean;
  error: string | null;
  step: GapFillStep;
  stepLabel: string;
  totalSteps: number;
  startedAt: number | null;
  cancelController: AbortController | null;

  /** Audit + gap context retained while the modal is open so /accept
   * can stamp provenance without re-passing it from the call site. */
  auditId: string | null;
  gap: Gap | null;
  candidates: GapFillCandidate[];

  start: (
    conceptId: string,
    jobId: string,
    auditId: string,
    gap: Gap,
  ) => void;
  /** Restore audit context lost on hard-refresh resume paths. The
   * /generate/active endpoint doesn't carry gap or audit_id, so the
   * SSE complete event payload echoes them back and we stamp them
   * here before flipping isOpen. Without this the modal would render
   * null (the `if (!gap) return null` early-return would trip). */
  setContext: (auditId: string, gap: Gap) => void;
  setStep: (step: GapFillStep, label: string) => void;
  setCancelController: (controller: AbortController | null) => void;
  setCandidates: (candidates: GapFillCandidate[]) => void;
  openModal: () => void;
  closeModal: () => void;
  fail: (error: string) => void;
  finish: () => void;
  reset: () => void;
}

const initialState = {
  conceptId: null,
  jobId: null,
  isPending: false,
  isCompleted: false,
  isOpen: false,
  error: null,
  step: 0 as GapFillStep,
  stepLabel: "",
  totalSteps: 3,
  startedAt: null,
  cancelController: null,
  auditId: null,
  gap: null as Gap | null,
  candidates: [] as GapFillCandidate[],
};

export const useGapFillStore = create<GapFillState>((set) => ({
  ...initialState,

  start: (conceptId, jobId, auditId, gap) =>
    set({
      ...initialState,
      conceptId,
      jobId,
      auditId,
      gap,
      isPending: true,
      startedAt: Date.now(),
      stepLabel: "Searching",
    }),

  setContext: (auditId, gap) => set({ auditId, gap }),

  setStep: (step, label) => set({ step, stepLabel: label }),

  setCancelController: (cancelController) => set({ cancelController }),

  setCandidates: (candidates) => set({ candidates }),

  openModal: () => set({ isOpen: true }),

  closeModal: () => set({ isOpen: false }),

  fail: (error) =>
    set({ isPending: false, error, step: 0, stepLabel: "" }),

  finish: () =>
    set({
      isPending: false,
      isCompleted: true,
      step: 3,
      stepLabel: "Complete",
      isOpen: true,
    }),

  reset: () => set({ ...initialState }),
}));
